'use client'

import { datadogRum } from '@datadog/browser-rum'

import type {
  AnalyticsAdapter,
  AnalyticsUserProperties,
  BaseEventProperties,
} from '../types'

/**
 * Datadog RUM adapter.
 *
 * Custom events become RUM *actions*, so they land alongside the views,
 * resources, errors, and Session Replay for the same session.
 *
 * Two things to know if you touch this file:
 *
 * 1. It writes with `setUserProperty` / `setGlobalContextProperty`, never
 *    `datadogRum.setUser(obj)`. `setUser` *replaces* the whole user object, so
 *    `identify()` and `setUserProperties()` would clobber each other.
 * 2. It is the single write path for RUM user + global context. Don't call
 *    `datadogRum.setUser` elsewhere — two writers racing on the same state is
 *    exactly the bug this adapter replaced.
 */
export class DatadogBrowserAdapter implements AnalyticsAdapter {
  /** Global-context keys this adapter has set, so `reset()` can clear them. */
  private globalContextKeys = new Set<string>()

  /** User-property keys this adapter has set, for the same reason. */
  private userPropertyKeys = new Set<string>()

  // No-op: `datadogRum.init()` already ran at module load in each app's
  // `instrumentation-client.ts`. RUM has no per-call API key.
  init(): void {
    /* no-op */
  }

  track(eventName: string, properties?: BaseEventProperties): void {
    datadogRum.addAction(eventName, properties)
  }

  identify(userId: string): void {
    this.setUserProperty('id', userId)
  }

  setUserProperties(properties: AnalyticsUserProperties): void {
    Object.entries(properties).forEach(([key, value]) => {
      this.setUserProperty(key, value)
    })
  }

  setDefaultProperties(properties: BaseEventProperties): void {
    Object.entries(properties).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        datadogRum.removeGlobalContextProperty(key)
        this.globalContextKeys.delete(key)
        return
      }

      datadogRum.setGlobalContextProperty(key, value)
      this.globalContextKeys.add(key)
    })
  }

  reset(): void {
    this.globalContextKeys.forEach((key) => {
      datadogRum.removeGlobalContextProperty(key)
    })
    this.globalContextKeys.clear()

    this.userPropertyKeys.clear()
    datadogRum.clearUser()
  }

  // RUM batches and flushes on its own; there is no public flush API.
  async flush(): Promise<void> {
    /* no-op */
  }

  private setUserProperty(
    key: string,
    value: string | number | boolean | undefined | null,
  ): void {
    if (value === undefined || value === null) {
      datadogRum.removeUserProperty(key)
      this.userPropertyKeys.delete(key)
      return
    }

    datadogRum.setUserProperty(key, value)
    this.userPropertyKeys.add(key)
  }
}
